import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Loader, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { industries } from '@/data/industries';

const API = '/api';
function getHeaders() { const t = localStorage.getItem('pkepl_token'); return t ? { Authorization: `Bearer ${t}` } : {}; }

const inp = "w-full px-3.5 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E5A57]/20 focus:border-[#0E5A57] font-sans bg-white text-[#1F2937]";
const ta = `${inp} resize-none`;

export default function AdminIndustries() {
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/industries`, { withCredentials: true, headers: getHeaders() })
      .then(r => {
        const map = {};
        const items = Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.items) ? r.data.items : []);
        items.forEach(item => { if (item.slug) map[item.slug] = item; });
        const d = {};
        industries.forEach(ind => {
          const ov = map[ind.slug] || {};
          d[ind.slug] = {
            title: ov.title || ind.title,
            tagline: ov.tagline || ind.tagline,
            description: ov.description || ind.description,
            seo_title: ov.seo_title || ind.seoTitle,
            seo_description: ov.seo_description || ind.seoDesc,
          };
        });
        setDrafts(d);
      })
      .catch(() => toast.error('Failed to load industries'))
      .finally(() => setLoading(false));
  }, []);

  const update = (slug, key, val) => setDrafts(d => ({ ...d, [slug]: { ...d[slug], [key]: val } }));

  const handleSave = async (slug) => {
    setSaving(slug);
    try {
      await axios.put(`${API}/admin/industries/${slug}`, drafts[slug], { withCredentials: true, headers: getHeaders() });
      toast.success('Industry saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader className="w-6 h-6 animate-spin text-[#0E5A57]" /></div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-industries-page">
      <div>
        <h1 className="font-serif text-2xl font-bold text-[#153E5C]">Industries</h1>
        <p className="text-sm text-[#6B7280] font-sans mt-1">Edit title, tagline, description, and SEO for each industry page. Changes are reflected on the public site.</p>
      </div>

      <div className="space-y-3">
        {industries.map((industry) => {
          const { slug } = industry;
          const draft = drafts[slug] || {};
          const isOpen = expanded === slug;
          const isSaving = saving === slug;
          const Icon = industry.icon;

          return (
            <div key={slug} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
              <button type="button" onClick={() => setExpanded(isOpen ? null : slug)}
                className="w-full flex items-center justify-between p-5 hover:bg-[#F7FAFC] transition-colors"
                data-testid={`industry-item-${slug}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${industry.color}18` }}>
                    <Icon className="w-4 h-4" style={{ color: industry.color }} />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-[#1F2937] font-sans text-sm">{draft.title || industry.title}</p>
                    <p className="text-xs text-[#9CA3AF] font-mono">/industries/{slug}</p>
                  </div>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />}
              </button>

              {isOpen && (
                <div className="border-t border-[#E5E7EB] p-5 space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">Title</label>
                      <input className={inp} value={draft.title || ''} onChange={e => update(slug, 'title', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">Tagline / Subtitle</label>
                      <input className={inp} value={draft.tagline || ''} onChange={e => update(slug, 'tagline', e.target.value)} />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">Description</label>
                    <textarea className={ta} rows={4} value={draft.description || ''} onChange={e => update(slug, 'description', e.target.value)} />
                  </div>

                  <div className="space-y-4 pt-4 border-t border-[#F3F4F6]">
                    <p className="text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">SEO</p>
                    <div className="space-y-1.5">
                      <label className="block text-xs text-[#6B7280] font-sans">SEO Title <span className="text-[#9CA3AF]">(50–60 chars recommended)</span></label>
                      <input className={inp} value={draft.seo_title || ''} onChange={e => update(slug, 'seo_title', e.target.value)} />
                      <p className="text-xs text-[#9CA3AF] font-sans">{(draft.seo_title || '').length} chars</p>
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-xs text-[#6B7280] font-sans">SEO Description <span className="text-[#9CA3AF]">(150–160 chars recommended)</span></label>
                      <textarea className={ta} rows={2} value={draft.seo_description || ''} onChange={e => update(slug, 'seo_description', e.target.value)} />
                      <p className="text-xs text-[#9CA3AF] font-sans">{(draft.seo_description || '').length} chars</p>
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <button onClick={() => handleSave(slug)} disabled={isSaving}
                      data-testid={`industry-save-${slug}`}
                      className="flex items-center gap-2 bg-[#0E5A57] hover:bg-[#2E7D7A] disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors">
                      {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
